import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Search, Download, Filter, Loader2,
  Users, Phone, Calendar, DollarSign, ArrowUpDown, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import AddPerformanceDataModal from '../components/admin/AddPerformanceDataModal';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SortableHeader = ({ field, label, onSort }) => (
  <th 
    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      <span className="truncate">{label}</span>
      <ArrowUpDown className="h-3 w-3 flex-shrink-0" />
    </div>
  </th>
);

export default function AdminPerformanceOverview() {
  const [clients, setClients] = useState([]);
  const [performanceData, setPerformanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('business_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [timeRange, setTimeRange] = useState(30);
  const [showAddDataModal, setShowAddDataModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all clients
      const clientsResponse = await fetch(`${API}/api/admin/clients`, {
        credentials: 'include'
      });
      
      if (clientsResponse.ok) {
        const clientsData = await clientsResponse.json();
        // Ensure clientsData is an array
        const clientsArray = Array.isArray(clientsData) ? clientsData : [];
        setClients(clientsArray);
        
        // Fetch performance data for each client
        const perfPromises = clientsArray.map(client =>
          fetch(`${API}/api/admin/performance/summary/${client.client_id}?days=${timeRange}`, {
            credentials: 'include'
          })
            .then(res => res.ok ? res.json() : null)
            .catch(() => null)
        );
        
        const perfResults = await Promise.all(perfPromises);
        
        // Map performance data by client_id
        const perfMap = {};
        clientsArray.forEach((client, index) => {
          if (perfResults[index]) {
            perfMap[client.client_id] = perfResults[index];
          }
        });
        
        setPerformanceData(perfMap);
      } else {
        console.error('Failed to fetch clients:', clientsResponse.status);
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedAndFilteredClients = () => {
    let filtered = clients.filter(client => {
      const searchLower = searchTerm.toLowerCase();
      return (
        client.business_name?.toLowerCase().includes(searchLower) ||
        client.short_id?.toLowerCase().includes(searchLower) ||
        client.primary_contact_first_name?.toLowerCase().includes(searchLower) ||
        client.primary_contact_last_name?.toLowerCase().includes(searchLower)
      );
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortField === 'business_name') {
        aVal = a.business_name || '';
        bVal = b.business_name || '';
      } else if (sortField === 'leads') {
        aVal = performanceData[a.client_id]?.total_leads || 0;
        bVal = performanceData[b.client_id]?.total_leads || 0;
      } else if (sortField === 'calls') {
        aVal = performanceData[a.client_id]?.total_calls || 0;
        bVal = performanceData[b.client_id]?.total_calls || 0;
      } else if (sortField === 'appointments') {
        aVal = performanceData[a.client_id]?.booked_appointments || 0;
        bVal = performanceData[b.client_id]?.booked_appointments || 0;
      } else if (sortField === 'spend') {
        aVal = performanceData[a.client_id]?.total_ad_spend || 0;
        bVal = performanceData[b.client_id]?.total_ad_spend || 0;
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    return filtered;
  };

  const exportToCSV = () => {
    const sortedClients = getSortedAndFilteredClients();
    
    const headers = ['Client Name', 'Client ID', 'Total Leads', 'Qualified Leads', 'Appointments', 'Total Calls', 'Answered Calls', 'Ad Spend', 'Contract Status'];
    
    const rows = sortedClients.map(client => {
      const perf = performanceData[client.client_id] || {};
      return [
        client.business_name || '',
        client.short_id || '',
        perf.total_leads || 0,
        perf.qualified_leads || 0,
        perf.booked_appointments || 0,
        perf.total_calls || 0,
        perf.answered_calls || 0,
        (perf.total_ad_spend || 0).toFixed(2),
        client.contract_status || 'draft'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-overview-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      signed_pending_payment: 'bg-yellow-100 text-yellow-800',
      on_hold: 'bg-orange-100 text-orange-800',
      draft: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {(status || 'draft').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const sortedClients = getSortedAndFilteredClients();

  // Calculate totals
  const totals = sortedClients.reduce((acc, client) => {
    const perf = performanceData[client.client_id] || {};
    return {
      leads: acc.leads + (perf.total_leads || 0),
      qualified: acc.qualified + (perf.qualified_leads || 0),
      appointments: acc.appointments + (perf.booked_appointments || 0),
      calls: acc.calls + (perf.total_calls || 0),
      spend: acc.spend + (perf.total_ad_spend || 0)
    };
  }, { leads: 0, qualified: 0, appointments: 0, calls: 0, spend: 0 });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
                Performance Overview
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                All clients performance metrics
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button onClick={() => setShowAddDataModal(true)} size="sm" className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm">
                <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Add Data
              </Button>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value={7}>Last 7 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={90}>Last 90 Days</option>
              </select>
              <Button onClick={exportToCSV} size="sm" className="text-xs sm:text-sm">
                <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-blue-500">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              <p className="text-xs sm:text-sm text-gray-600">Total Leads</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{totals.leads.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-green-500">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              <p className="text-xs sm:text-sm text-gray-600">Qualified</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{totals.qualified.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-purple-500">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              <p className="text-xs sm:text-sm text-gray-600">Appointments</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{totals.appointments.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-yellow-500">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
              <p className="text-xs sm:text-sm text-gray-600">Total Calls</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{totals.calls.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-red-500">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
              <p className="text-xs sm:text-sm text-gray-600">Total Spend</p>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(totals.spend)}</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow mb-4 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
            <Input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 text-sm"
            />
            {searchTerm && (
              <Button variant="ghost" size="sm" onClick={() => setSearchTerm('')} className="text-xs sm:text-sm flex-shrink-0">
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader field="business_name" label="Client" onSort={handleSort} />
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">ID</th>
                    <SortableHeader field="leads" label="Leads" onSort={handleSort} />
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Qualified</th>
                    <SortableHeader field="appointments" label="Appts" onSort={handleSort} />
                    <SortableHeader field="calls" label="Calls" onSort={handleSort} />
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Answered</th>
                    <SortableHeader field="spend" label="Spend" onSort={handleSort} />
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedClients.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-gray-500 text-sm">
                        No clients found
                      </td>
                    </tr>
                  ) : (
                    sortedClients.map(client => {
                      const perf = performanceData[client.client_id] || {};
                      
                      return (
                        <tr key={client.client_id} className="hover:bg-gray-50">
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div>
                              <div className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">
                                {client.business_name || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500 sm:block hidden">
                                {client.primary_contact_first_name} {client.primary_contact_last_name}
                              </div>
                              <div className="text-xs text-gray-500 sm:hidden">
                                ID: {client.short_id}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                            {client.short_id}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-blue-600">
                            {perf.total_leads || 0}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-green-600 hidden md:table-cell">
                            {perf.qualified_leads || 0}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-purple-600">
                            {perf.booked_appointments || 0}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-yellow-600">
                            {perf.total_calls || 0}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden lg:table-cell">
                            {perf.answered_calls || 0}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-gray-900">
                            <span className="hidden sm:inline">{formatCurrency(perf.total_ad_spend || 0)}</span>
                            <span className="sm:hidden">${((perf.total_ad_spend || 0) / 1000).toFixed(1)}k</span>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                            {getStatusBadge(client.contract_status)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="mt-4 text-xs sm:text-sm text-gray-600 px-2 sm:px-0">
          Showing {sortedClients.length} of {clients.length} clients
        </div>
      </main>

      {/* Add Performance Data Modal */}
      <AddPerformanceDataModal
        isOpen={showAddDataModal}
        onClose={() => setShowAddDataModal(false)}
        clients={clients}
        onSuccess={fetchData}
      />
    </div>
  );
}
